/**
 * Local type definitions for the PartyBuyingPreference parameters/intents JSONObjects.
 *
 * The GraphQL schema declares these as JSONObject (untyped). These interfaces
 * provide client-side type safety for the known structure returned by resi-ms-profile.
 *
 * IMPORTANT: ms-profile uses Jackson with snake_case naming strategy (via ssp-common-lib).
 * All JSON keys are snake_case over the wire.
 *
 * parameters: aligned 1:1 with seek ListingCompatibilityFilters.
 * intents: user-stated preference including NOT_SURE/ALL values (not filterable).
 */

/**
 * Filter parameters stored in PartyBuyingPreference.parameters.
 * All values are strings to align with seek DSL query params.
 * Keys are snake_case (Jackson serialization from ms-profile).
 */
export interface BuyerPreferenceParameters {
  price_min?: string | null
  price_max?: string | null
  occ_status?: string | null
  usecode_product_type?: string | null
  beds_min?: string | null
  baths_min?: string | null
  yr_bt_min?: string | null
  usecode_property_type?: string | null
  locations?: string | null
  preference_notes?: string | null
}

/**
 * Intent fields stored in PartyBuyingPreference.intents.
 * Tracks the user's stated preference per category (may include NOT_SURE/ALL
 * which are non-filterable and NOT stored in parameters).
 * Dropdown keys (price/beds/baths/year built) additionally carry either an
 * intent value (ANY/NO_MIN/NO_MAX = explicit no-constraint) or a mirror of
 * the concrete value saved in parameters, so the backend can distinguish
 * "never touched" from "explicitly unconstrained" (MERCH-126).
 * Keys are snake_case (Jackson serialization from ms-profile).
 */
export interface BuyerPreferenceIntents {
  occ_status?: string | null
  usecode_product_type?: string | null
  usecode_property_type?: string | null
  price_min?: string | null
  price_max?: string | null
  beds_min?: string | null
  baths_min?: string | null
  yr_bt_min?: string | null
}

// --- Enum-like constants for intent values ---

export const OCC_STATUS_VALUES = [
  'VACANT',
  'OCCUPIED',
  'ALL',
  'NOT_SURE',
] as const
export type OccStatusValue = (typeof OCC_STATUS_VALUES)[number]

export const SALE_TYPE_VALUES = [
  'FORECLOSURE',
  'BANK_OWNED',
  'PRIVATE_SELLER',
  'ALL',
  'NOT_SURE',
] as const
export type SaleTypeValue = (typeof SALE_TYPE_VALUES)[number]

export const PROPERTY_TYPE_VALUES = [
  'SINGLE_FAMILY',
  'CONDOS_AND_TOWNHOUSES',
  'MULTI_FAMILY',
  'LAND',
  'ALL',
  'NOT_SURE',
] as const
export type PropertyTypeValue = (typeof PROPERTY_TYPE_VALUES)[number]

// Mapping from intent sale type to seek usecode_product_type filter codes.
// These are passed to mapListingFiltersToSeekDsl which maps them to
// listing_configuration.asset_type.keyword values.
export const SALE_TYPE_TO_PRODUCT_TYPES: Record<string, string[]> = {
  FORECLOSURE: ['resi_ft'],
  BANK_OWNED: ['resi_bo_v2', 'resi_new_foreclosed'],
  PRIVATE_SELLER: ['resi_ps'],
}

// Mapping from intent property type to seek usecode_property_type values
export const PROPERTY_TYPE_TO_FILTER_VALUE: Record<string, string> = {
  SINGLE_FAMILY: 'resi_sfr',
  CONDOS_AND_TOWNHOUSES: 'resi_condo',
  MULTI_FAMILY: 'resi_mfr',
  LAND: 'resi_land',
}

// Reverse mappings: seek codes → intent values (for read-back display)
export const PRODUCT_TYPES_TO_SALE_TYPE: Record<string, string> =
  Object.entries(SALE_TYPE_TO_PRODUCT_TYPES).reduce((acc, [intent, codes]) => {
    codes.forEach(code => {
      acc[code] = intent
    })
    return acc
  }, {} as Record<string, string>)

export const FILTER_VALUE_TO_PROPERTY_TYPE: Record<string, string> =
  Object.entries(PROPERTY_TYPE_TO_FILTER_VALUE).reduce(
    (acc, [intent, code]) => {
      acc[code] = intent
      return acc
    },
    {} as Record<string, string>
  )
